import React, { Fragment, useState } from 'react'
import { useTranslation } from "react-i18next";
import { Button, Col, Input, Label, Row } from 'reactstrap'
import Select from "react-select";
import SelectCustomStyles from '../../../../../utils/styles/reactSelect';
import DatePicker from 'react-datepicker';
import { useSettings } from '../../../../../context/settings';
import ShiftHoursModal from '../Modals/ShiftHours';
import { isDayOff, shiftMeta, totalHours } from '../../../utils/shifts';

function ContractTab({ employeeData, setEmployeeData }) {
  const { t } = useTranslation();
  const { datePickerFormat, formatDate, formatTime } = useSettings();
  const [shiftHoursModalOpen, setShiftHoursModalOpen] = useState(false);

  const shiftHours = employeeData.shift_hours;
  const isLoop = shiftHours?.mode === 'loop';

  const shiftDays = !shiftHours
    ? []
    : isLoop
      ? (shiftHours.loop?.days || []).map((d, i) => ({ ...d, key: `loop-${i}`, label: `${t('hr.day')} ${i + 1}` }))
      : (shiftHours.default || []).map((d) => ({ ...d, key: d.day, label: t(`hr.${d.day}`) }));

  const shiftSubtitle = [
    isLoop ? t('hr.cycleLength', { count: shiftDays.length }) : t('hr.weekly'),
    `${totalHours(shiftDays)}h`,
    isLoop && shiftHours?.loop?.anchor ? `${t('hr.cycleStart')}: ${formatDate(shiftHours.loop.anchor)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Fragment>
      <Row>
        <Col md="4" className="mb-3">
          <Label>{t('hr.contractType')}</Label>
          <Input type="select" value={employeeData.contract_type || ''} onChange={(e) => setEmployeeData({ ...employeeData, contract_type: e.target.value })}>
            <option value="cdd">{t('hr.cdd')}</option>
            <option value="cdi">{t('hr.cdi')}</option>
          </Input>
        </Col>
        <Col md="4" className="mb-3">
          <Label>{t('hr.contractStart')}</Label>
          <DatePicker
            selected={employeeData.contract_start_date}
            onChange={(date) => setEmployeeData({ ...employeeData, contract_start_date: date })}
            className="form-control"
            dateFormat={datePickerFormat}
            placeholderText={datePickerFormat.toLowerCase()}
          />
        </Col>
        {employeeData.contract_type === 'cdd' && (
          <Col md="4" className="mb-3">
            <Label>{t('hr.contractEnd')}</Label>
            <DatePicker
              selected={employeeData.contract_end_date}
              onChange={(date) => setEmployeeData({ ...employeeData, contract_end_date: date })}
              className="form-control"
              dateFormat={datePickerFormat}
              placeholderText={datePickerFormat.toLowerCase()}
            />
          </Col>
        )}
        <Col md="4" className="mb-3">
          <Label>{t('hr.workSchedule')}</Label>
          <Input type="select" value={employeeData.work_schedule || ''} onChange={(e) => setEmployeeData({ ...employeeData, work_schedule: e.target.value })}>
            <option value="fulltime">{t('hr.fullTime')}</option>
            <option value="parttime">{t('hr.partTime')}</option>
            <option value="shiftpatterns">{t('hr.shiftPatterns')}</option>
          </Input>
        </Col>
        <Col md="4" className="mb-3">
          <Label>{t('hr.weeklyHours')}</Label>
          <Input 
            type="number" 
            min="0" 
            value={employeeData.weekly_working_hours || ''}
            onChange={(e) => setEmployeeData({ ...employeeData, weekly_working_hours: e.target.value })}
          />
        </Col>
        <Col md="4" className="mb-3">
          <Label>{t('hr.shiftHours')}</Label>
          <Button color="primary" block outline onClick={() => setShiftHoursModalOpen(true)}>
            {shiftHours ? t('common.edit') : t('hr.setTimes')}
          </Button>
        </Col>

        {shiftHours && (
          <Col md="12" className="mb-3">
            <div className="border rounded p-3">
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <span className="badge bg-primary">{isLoop ? t('hr.loop') : t('hr.default')}</span>
                <small className="text-muted">{shiftSubtitle}</small>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {shiftDays.map((d) => {
                  const off = isDayOff(d);
                  const meta = off ? null : shiftMeta(d.start, d.end);
                  return (
                    <span key={d.key} className={`border rounded px-2 py-1 small ${off ? 'text-muted' : ''}`}>
                      <span className="fw-bold">{d.label}</span>{' '}
                      {off ? t('hr.restDay') : `${formatTime(d.start)} - ${formatTime(d.end)}`}
                      {meta?.overnight && (
                        <span className="badge bg-warning text-dark ms-1">{t('hr.nextDay')}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          </Col>
        )}

        <h5 className="fw-bold mt-4">{t('hr.salary')}</h5>

        <Col md="4" className="mb-3">
          <Label>{t('hr.contractStart')}</Label>
          <Input type="select" value={employeeData.salary_frequency || ''} onChange={(e) => setEmployeeData({ ...employeeData, salary_frequency: e.target.value })}>
            <option value="monthly">{t('hr.monthly')}</option>
            <option value="weekly">{t('hr.weekly')}</option>
            <option value="biweekly">{t('hr.biweekly')}</option>
          </Input>
        </Col>
      </Row>

      <ShiftHoursModal
        isOpen={shiftHoursModalOpen}
        toggle={() => setShiftHoursModalOpen(!shiftHoursModalOpen)}
        value={employeeData.shift_hours}
        onSave={(shiftHours) => setEmployeeData({ ...employeeData, shift_hours: shiftHours })}
      />
    </Fragment>
  )
}

export default ContractTab
